import { observable, action, computed, reaction } from 'mobx';
import { carsHelper, QsHelper } from '../helpers';
import lunr from 'lunr';
import { isFunction, flatten, omit } from 'lodash';

import { uniqWith, uniq } from 'lodash';

import { api } from '../services';

import { filterStore } from 'air-filter-mobx-store';
import authStore from '../stores/authStore';
import { commonStore } from './index';

const uniqByField = fieldname => (el1, el2) => el1[fieldname] === el2[fieldname];
const sortByField = fieldname => (el1, el2) => (el1[fieldname] === el2[fieldname] ? 0 : el1[fieldname] < el2[fieldname] ? -1 : 1);
const mapByField = fieldname => el => el[fieldname];
const floorMappingFn = num => Math.floor(num);
const ceilMappingFn = num => Math.ceil(num);

class CarsStore {
  @observable
  cars = [];
  @observable loadedAt;
  SAFETY_ALARM_CODES = ['ABS_WARNING', 'PASSENGER_AIRBAG_WARNING', 'TIRE_PRESSURE_WARNING', 'AIRBAG_WARNING', 'BRAKE_LEVEL_WARNING', 'LAMP_WARNING'];

  @observable sortingFunction = carsHelper.sortByWeight;

  @observable
  searchQuery = null;

  @observable
  allDealers = [];

  @observable
  isLoading = false;

  @observable
  virtualCars = [];

  @observable
  virtualCarsCount = 0;

  @observable
  virtualIsLoading = false;

  @observable
  virtualQuery = '';

  @observable
  virtualSortAndOrder;

  @observable
  cacheKey;

  @observable
  cacheFidelityData;

  @observable
  filtersOptions;

  @observable
  virtualBadges = { withAlarmsAndSafety: 0, withLowBattery: 0, withDtcsRedPlusOrange: 0, withMaintenances: 0, withHelpRequests: 0 };

  constructor({ filterStore, authStore, commonStore }) {
    this.filterStore = filterStore;
    this.authStore = authStore;
    this.commonStore = commonStore;

    reaction(
      // () => this.filterStore.globalFilterFunction,
      () => this.filterStore.globalFilterFunctionFromPage('cars'),
      fn => {
        window.localStorage.setItem(this.cacheKey, 1);
        this.virtualLoad().then(response => {
          this.virtualLoadBadges();
        });

        const cars = this.cars.map(car => {
          if (fn(car)) {
            return { ...car, hidden: false };
          } else {
            return { ...car, hidden: true };
          }
        });
        this.setCars(cars);
      }
    );
    this.authStore = authStore;
    reaction(
      () => this.authStore.isAdmin,
      isAdmin => {
        if (isAdmin) {
          this.loadDealers();
        }
      },
      { fireImmediately: true }
    );
    reaction(
      () => this.commonStore.selectedDealers,
      fn => {
        // this.load();
        this.virtualLoad().then(response => {
          this.virtualLoadBadges();
        });
      }
    );
    reaction(
      () => this.virtualQuery,
      fn => {
        this.virtualLoadBadges();
      }
    );
  }

  buildIndex(cars) {
    this.index = lunr(function() {
      this.field('client.fullName');
      this.field('plate');
      this.field('make.descrizione');
      this.field('model.descrizione');
      this.ref('id');
      cars.forEach(car => {
        car['client.fullName'] = car.client.fullName;
        car['make.descrizione'] = car.make.descrizione;
        car['model.descrizione'] = car.model.descrizione;
        this.add(car);
      });
    });
    return cars;
  }

  @action
  load() {
    this.isLoading = true;
    const p1 = api('get all fidelity');
    const p2 = new Promise(resolve => {
      if (this.authStore.isAdmin) {
        return api('get cars list', this.commonStore.selectedDealers).then(res => {
          resolve(res);
        });
      } else {
        return api('get cars').then(res => {
          resolve(res);
        });
      }
    });
    return Promise.all([p1, p2])
      .then(([fidelities, cars]) =>
        cars.map(car => ({
          ...car,
          leadId: car.leads && car.leads.length > 0 ? car.leads[0].id : null,
          client: { ...car.client, fidelityIndex: (car.client.fidelityIndex = fidelities[car.client.id] || null) }
        }))
      )
      .then(cars => this.buildIndex(cars))
      .then(cars => cars.map(car => ({ ...car, weight: carsHelper.weight(car) })))
      .then(cars => cars.map(car => ({ ...car, daysToM: carsHelper.daysToM(car) })))
      .then(cars => cars.map(car => ({ ...car, statusWeight: carsHelper.statusWeight(car) })))
      .then(cars => {
        // const { globalFilterFunction } = this.filterStore;
        const globalFilterFunction = this.filterStore.globalFilterFunctionFromPage('cars');
        cars = cars.map(car => {
          return globalFilterFunction instanceof Function ? (globalFilterFunction(car) ? { ...car, hidden: false } : { ...car, hidden: true }) : car;
        });
        return cars;
      })
      .then(cars => cars.sort(carsHelper.sortByWeight))
      .then(cars => {
        this.setCars(cars);
        this.setLoadedAt();
        this.isLoading = false;
        // TODO to restore
        //   setTimeout(() => {
        //     this.buildIndex(cars);
        //   }, 500);
      });
  }

  @action setLoadedAt() {
    this.loadedAt = new Date().getTime();
  }

  @action
  setCars(cars) {
    this.cars = cars;
  }

  @action virtualLoad({ page = 1 } = {}) {
    this.virtualIsLoading = true;
    const { filterStore, commonStore } = this;

    let filtersCollection = {};
    Object.keys(filterStore.appliedFiltersParameters).map(filterKey => {
      if (['carStatus_filter'].includes(filterKey)) {
        filtersCollection['status'] = filterStore.appliedFiltersParameters[filterKey];
      } else {
        filtersCollection[filterKey.replace('carFilter_', '')] = filterStore.appliedFiltersParameters[filterKey];
      }
    });
    if (commonStore.selectedDealers.length > 0) {
      filtersCollection['dealers'] = commonStore.selectedDealers;
    }
    delete filtersCollection['search.query'];
    const qs = new QsHelper(window.location.search),
      filters = qs.serializeObject({ filters: filtersCollection });

    let sort = '',
      order = '';
    if (this.virtualSortAndOrder) {
      ({ sort, order } = this.virtualSortAndOrder);
    }

    let query = this.virtualQuery;

    const p1 = this.cacheFidelityData === undefined ? api('get all fidelity') : Promise.resolve(this.cacheFidelityData);
    const p2 = api('getCarsList', { page, sort, order, filters, query });
    const p3 =
      this.filtersOptions === undefined
        ? api('getCarsFiltersOptions').then(filtersOptions => {
            this.filtersOptions = filtersOptions;
            return filtersOptions;
          })
        : Promise.resolve(this.filtersOptions);
    return Promise.all([p1, p2, p3])
      .then(([fidelityData, carsData, filtersOptions]) => {
        if (this.cacheFidelityData === undefined) {
          this.cacheFidelityData = fidelityData;
        }
        carsData['rows'] = carsData['rows'].map(car => ({
          ...car,
          leadId: car.leads && car.leads.length > 0 ? car.leads[0].id : null,
          client: { ...car.client, fidelityIndex: (car.client.fidelityIndex = fidelityData[car.client.id] || null) }
        }));
        return carsData;
      })
      .then(carsData => {
        this.setVirtualData({ rows: carsData.rows, count: carsData.count });
        return Promise.resolve(carsData);
      })
      .catch(error => {
        return Promise.reject(error);
      })
      .finally(() => {
        this.virtualIsLoading = false;
      });
  }

  @action
  setVirtualData({ rows, count }) {
    this.virtualCars = rows;
    this.virtualCarsCount = count;
  }

  @action
  setVirtualQuery(query = '') {
    query = query.trim(query);
    this.virtualQuery = query;
  }

  @action
  setVirtualSortAndOrder({ sort, order }) {
    order = order === 'ascend' ? 'ASC' : order === 'descend' ? 'DESC' : undefined;
    this.virtualSortAndOrder = sort !== undefined && order !== undefined ? { sort, order } : undefined;
  }

  @action virtualLoadBadges() {
    const { filterStore, commonStore } = this;

    let filtersCollection = {};
    Object.keys(filterStore.appliedFiltersParameters).map(filterKey => {
      if (['carStatus_filter'].includes(filterKey)) {
        filtersCollection['status'] = filterStore.appliedFiltersParameters[filterKey];
      } else {
        filtersCollection[filterKey.replace('carFilter_', '')] = filterStore.appliedFiltersParameters[filterKey];
      }
    });
    if (commonStore.selectedDealers.length > 0) {
      filtersCollection['dealers'] = commonStore.selectedDealers;
    }
    delete filtersCollection['search.query'];
    const qs = new QsHelper(window.location.search),
      filters = qs.serializeObject({ filters: filtersCollection });

    api('getCarsListBadges', { filters, query: this.virtualQuery }).then(badges => (this.virtualBadges = badges));
  }

  @action loadDealers() {
    api('getAllDealers').then(dealers => {
      this.allDealers = dealers;
    });
  }

  @computed get filtered() {
    return this.cars.filter(car => !car.hidden).sort(this.sortingFunction);
  }

  filterLength = (filterType, attribute) => {
    if (isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this[attribute].filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    return this[attribute].length;
  };

  @computed get dtcsRed() {
    return flatten(this.withDtcsRed.map(lead => lead.dtcs)).filter(dtc => dtc.severity === 'red');
  }

  @computed get dtcsOrange() {
    return flatten(this.withDtcsOrange.map(lead => lead.dtcs)).filter(dtc => dtc.severity === 'orange');
  }

  @computed get withDtcsRedPlusOrange() {
    return this.cars.filter(this.withDtcsRedPlusOrangeFilterFn);
  }

  @computed get withLowBattery() {
    return this.cars.filter(this.withLowBatteryFilterFn);
  }

  @computed get withAlarmsAndSafety() {
    return this.cars.filter(this.withAlarmsAndSafetyFilterFn);
  }

  @computed get withMaintenances() {
    return this.cars.filter(this.withMaintenancesFilterFn);
  }

  @computed get withHelpRequests() {
    return this.cars.filter(this.withHelpRequestsFilterFn);
  }

  alarmsFilterFn = car => car.alarms.length > 0;

  withDtcsRedFilterFn = car => car.dtcs.findIndex(dtc => dtc.severity === 'red') > -1;

  withDtcsOrangeFilterFn = car => car.dtcs.findIndex(dtc => dtc.severity === 'orange') > -1;

  withDtcsRedPlusOrangeFilterFn = car => car.dtcs.length > 0;

  withLowBatteryFilterFn = car => (car.battery && car.battery.value < 11000 && car.battery.updatedAt && car.battery.updatedAt != '');

  withSafetyFilterFn = car => car.alarms.filter(alarm => this.SAFETY_ALARM_CODES.includes(alarm.code)).length > 0;

  withAlarmsAndSafetyFilterFn = car => car.alarms.length > 0 || car.alarms.filter(alarm => this.SAFETY_ALARM_CODES.includes(alarm.code)).length > 0;

  withMaintenancesFilterFn = car => car.maintenances.length > 0;

  withHelpRequestsFilterFn = car => car.helpRequests.length > 0;

  searchQueryFilterFn = car => {
    if (this.lunrResultAsObj && this.lunrResultAsObj[car.id]) {
      return true;
    }
    return false;
  };

  @action
  setFullTextSearch(textSearch) {
    if (!textSearch) {
      this.lunrResultAsObj = null;
      return;
    }
    const lunrResult = this.index.search(textSearch);
    let lunrResultAsObj = {};
    lunrResult.forEach(match => {
      lunrResultAsObj[match.ref] = match;
    });

    this.lunrResultAsObj = lunrResultAsObj;
  }

  @action
  setSortingFunction(fn = carsHelper.sortByWeight) {
    this.sortingFunction = fn;
  }

  search(textToSearch) {
    if (!textToSearch) {
      this.filterStore.remove('search.query');
      setTimeout(() => {
        this.filterStore.apply();
        this.setSortingFunction();
      }, 10);
      this.searchQuery = null;
      return;
    }
    this.filterStore.remove('search.query');
    this.setFullTextSearch(textToSearch);
    this.filterStore.add('search.query', this.searchQueryFilterFn);
    setTimeout(() => {
      this.filterStore.apply();
      const sortingFn = (car1, car2) => (this.lunrResultAsObj[car2.id] || {}).score - (this.lunrResultAsObj[car1.id] || {}).score;
      this.setSortingFunction(sortingFn);
    }, 10);
    this.searchQuery = textToSearch;
  }

  // @computed get areFiltered() {
  //   const prefix = 'carFilter_';
  //   const filters = Object.keys(this.filterStore.filters).filter(key => key.indexOf(prefix) === 0);
  //   return filters.length > 0;
  //   // return this.filterStore.hasAppliedFilters;
  // }
  @computed get filters() {
    return this.filterStore.filters;
  }

  getModelsFromMake(codiceMarca) {
    if (codiceMarca) {
      const allmodels = this.filtered.filter(car => car.make.codiceMarca === codiceMarca).map(mapByField('model'));
      return uniqWith(allmodels, uniqByField('codiceModello')).sort(sortByField('descrizione'));
    }
    return [];
  }

  getModelsFromMakeDescription(codiceMarca) {
    if (codiceMarca) {
      const allmodels = this.cars.filter(car => car.make.codiceMarca === codiceMarca).map(mapByField('model'));
      return uniqWith(allmodels, uniqByField('codiceModello')).sort(sortByField('descrizione'));
    }
    return [];
  }

  getTypesFromModel(codiceModello) {
    if (codiceModello) {
      const alltypes = this.filtered.filter(car => car.model.codiceModello === codiceModello).map(mapByField('type'));
      return uniqWith(alltypes, uniqByField('codiceInfocarAM')).sort(sortByField('descrizione'));
    }
    return [];
  }

  getTypesFromModelDescription(codiceModello) {
    if (codiceModello) {
      const alltypes = this.cars.filter(car => car.model.codiceModello === codiceModello).map(mapByField('type'));
      return uniqWith(alltypes, uniqByField('codiceInfocarAM')).sort(sortByField('descrizione'));
    }
    return [];
  }

  @computed
  get makes() {
    return uniqWith(this.filtered.map(mapByField('make')).filter(make => !!make.codiceMarca), uniqByField('codiceMarca')).sort(sortByField('descrizione'));
  }

  @computed
  get makesDescription() {
    return flatten(this.makesWithModels.map(make => omit(make, ['models']))).sort(sortByField('descrizione'));
  }

  @computed
  get models() {
    // return flatten(this.makesWithModels.map(mapByField('models')));
    return flatten(this.makesWithModels.map(mapByField('models')).map(models => models.map(model => omit(model, ['types']))));
  }

  @computed
  get modelsDescription() {
    // return flatten(this.makesWithModelsDescription.map(mapByField('models')));
    return flatten(this.makesWithModelsDescription.map(mapByField('models')).map(models => models.map(model => omit(model, ['types']))));
  }

  @computed
  get types() {
    return flatten(this.makesWithModels.map(mapByField('models')).map(models => flatten(models.map(mapByField('types')))));
  }

  @computed
  get typesDescription() {
    return flatten(this.makesWithModelsDescription.map(mapByField('models')).map(models => flatten(models.map(mapByField('types')))));
  }

  @computed
  get makesWithModels() {
    // // return this.makes.map(make => ({ ...make, models: this.getModelsFromMake(make.codiceMarca) }));
    // return this.makes.map(make => ({
    //   ...make,
    //   models: this.getModelsFromMake(make.codiceMarca).map(model => ({ ...model, types: this.getTypesFromModel(model.codiceModello) }))
    // }));
    return this.filtersOptions.makesModels;
  }

  @computed
  get makesWithModelsDescription() {
    // // return this.makesDescription.map(make => ({ ...make, models: this.getModelsFromMakeDescription(make.codiceMarca) }));
    // return this.makesDescription.map(make => ({
    //   ...make,
    //   models: this.getModelsFromMakeDescription(make.codiceMarca).map(model => ({ ...model, types: this.getTypesFromModelDescription(model.codiceModello) }))
    // }));
    return this.filtersOptions.makesModels;
  }

  @computed
  get dealers() {
    // const filteredDealerId = uniq(this.filtered.map(mapByField('dealerId')));
    // return this.allDealers.filter(dealer => filteredDealerId.includes(dealer.id)).sort(sortByField('name'));
    return this.filtersOptions.dealer;
  }

  @computed
  get alimentations() {
    // return uniq(this.filtered.map(mapByField('alimentation'))).sort();
    return this.filtersOptions.alimentation;
  }

  @computed get odometers() {
    return this.filtered
      .map(car => {
        if (car.km) {
          return car.km;
        } else {
          return -1;
        }
      })
      .filter(odo => odo > -1);
  }

  @computed get mileages() {
    return this.filtered
      .map(car => {
        if (car.odometer.value > 0) {
          return car.odometer.value;
        } else if (car.gpsOdometer.value > 0) {
          return car.gpsOdometer.value + car.initialOdometer;
        } else {
          return -1;
        }
      })
      .filter(value => value > -1);
  }

  @computed get registrationYears() {
    // return uniq(this.filtered.map(car => (car.registrationYear ? car.registrationYear : -1)).filter(odo => odo > -1)).sort();
    return this.filtersOptions.registrationYears.sort();
  }

  @computed get subscriptionStart() {
    return this.filtered.map(car => (car.subscriptionStart ? car.subscriptionStart : -1)).filter(odo => odo > -1);
  }

  @computed get registrationYearMin() {
    return Math.min(...this.registrationYears);
  }

  @computed get registrationYearMax() {
    return Math.max(...this.registrationYears);
  }

  @computed get mileagesPerDay() {
    return this.filtered
      .map(car => {
        if (car.kmPerDay > 0) {
          return car.kmPerDay;
        } else {
          return -1;
        }
      })
      .filter(value => value > -1);
  }

  @computed get minKmAvg() {
    // return this.mileagesPerDay.length > 0 ? Math.min(...this.mileagesPerDay.map(km => floorMappingFn(km / 10) * 10)) : 0;
    return this.filtersOptions.avgKm.minKmAvg;
  }

  @computed get maxKmAvg() {
    // return this.mileagesPerDay.length > 0 ? Math.max(...this.mileagesPerDay.map(km => ceilMappingFn(km / 10) * 10)) : 0;
    return this.filtersOptions.avgKm.maxKmAvg;
  }

  @computed get totalMilMin() {
    // return this.mileages.length > 0 ? Math.min(...this.mileages.map(km => floorMappingFn(km / 1000) * 1000)) : 0;
    return this.filtersOptions.mileage.minMileage;
  }

  @computed get totalMilMax() {
    // return this.mileages.length > 0 ? Math.max(...this.mileages.map(km => ceilMappingFn(km / 1000) * 1000)) : 0;
    return this.filtersOptions.mileage.maxMileage;
  }

  @computed get maintenanceType() {
    //
    return this.filtersOptions.maintenance;
  }

  @computed get startMonthToM() {
    //
    return this.filtersOptions.startMonthToM;
  }

  @computed get endMonthToM() {
    //
    return this.filtersOptions.endMonthToM;
  }

  @computed get startMonthSub() {
    //
    return this.filtersOptions.startMonthSub;
  }

  @computed get endMonthSub() {
    //
    return this.filtersOptions.endMonthSub;
  }

  @action
  setCacheKey({ pageView, querystring }) {
    this.cacheKey = `io.myair.mydesk.cachedpage.${pageView}${querystring ? querystring : ''}`;
  }
}

export default new CarsStore({ filterStore, authStore, commonStore });
